/**
 * Zustand Store for Image Processing Job Management
 *
 * This store manages the state related to image processing jobs initiated
 * by the user. It handles:
 * - Uploading the image file to the backend API (`/process`).
 * - Storing the job ID received from the backend.
 * - Subscribing to Supabase Realtime updates for the specific job ID.
 * - Updating the job status (`queued`, `processing`, `completed`, `failed`), progress,
 *   and potential errors based on Realtime messages.
 * - Storing the URL of the final generated image upon successful completion.
 * - Providing actions to start uploads and reset the state.
 * - Persisting essential job details (`jobId`, `jobStatus`, `resultImage`) to
 *   localStorage to handle page reloads, including resuming Realtime subscriptions
 *   for ongoing jobs or fetching missing results for completed jobs.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import apiClient from "../services/apiClient";
import { supabase } from "../main";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { AxiosError } from "axios";

/**
 * Represents the possible states of an image processing job.
 */
type JobStatus = "queued" | "processing" | "completed" | "failed";

/**
 * Defines the structure for job status information received from the backend.
 */
interface JobStatusResponse {
  /** The current status of the job. */
  status: JobStatus;
  /** The completion progress percentage (0-100). */
  progress: number;
  /** Any error message associated with the job (primarily for 'failed' status). */
  error?: string;
  /** The user ID associated with the job (needed for constructing the result image URL). */
  user_id?: string;
  /** The unique ID of the generated image (needed for constructing the result image URL). */
  image_id?: string;
}

/**
 * Represents the expected structure of a row from the 'jobs' database table
 * based on the fields used in the Realtime subscription callback.
 */
interface JobTableRow {
  status: JobStatus;
  progress?: number;
  error?: string;
  user_id?: string;
  image_id?: string;
  // Add other fields from your 'jobs' table if needed
}

/**
 * Defines the state structure and actions for the job processing store.
 */
interface JobState {
  // --- State ---
  /** The unique identifier of the currently active processing job. Null if no job is active. */
  jobId: string | null;
  /** The latest status information received for the active job via Realtime updates. Null initially. */
  jobStatus: JobStatusResponse | null;
  /** The final, persistent URL of the generated image from S3 storage. Null until job completes successfully. */
  resultImage: string | null;
  /** Error message key or details related to upload, processing, or connection errors. Null if no error. */
  error: string | null;
  /** Flag indicating if an image upload to the backend is currently in progress. */
  isUploading: boolean;
  /** Holds the active Supabase Realtime channel object for the current job. Null if not subscribed. */
  realtimeChannel: RealtimeChannel | null;
  /** Timestamp of the last relevant state update for expiry purposes. */
  lastUpdated: number | null;

  // --- Actions ---
  /** Manually sets the upload loading state indicator. */
  setIsUploading: (uploading: boolean) => void;
  /** Sets the error message state, typically with a translation key or error detail. */
  setError: (error: string | null) => void;
  /** Initiates the image upload and processing job. */
  startUpload: (file: File, style: string) => Promise<void>;
  /** Resets the entire job state to initial values and cleans up subscriptions. */
  resetJobState: () => void;
  /** Fallback action to fetch job results, mainly used on hydration if the result URL is missing. */
  fetchJobResult: (jobId: string) => Promise<void>;

  // --- Internal Actions (prefixed with _) ---
  /** Internal: Sets the job ID state. */
  _setJobId: (id: string | null) => void;
  /** Internal: Sets the job status state. */
  _setJobStatus: (status: JobStatusResponse | null) => void;
  /** Internal: Sets the final result image URL state. */
  _setResultImage: (image: string | null) => void;
  /** Internal: Subscribes to Supabase Realtime updates for the given job ID. */
  _subscribeToJob: (jobId: string) => void;
  /** Internal: Unsubscribes from the current Supabase Realtime channel. */
  _unsubscribeFromJob: () => void;
}

// Initial state values
const initialState = {
  jobId: null,
  jobStatus: null,
  resultImage: null,
  error: null,
  isUploading: false,
  realtimeChannel: null,
  lastUpdated: null,
};

// Reset state helper function
// Use a more generic type for 'set' compatible with both contexts
const resetState = (set: (state: Partial<JobState>) => void) => {
  set(initialState);
  // Attempt to reset the file input UI element
  // This might be better handled via React refs passed into the store or controlled components,
  // but direct DOM manipulation is kept here for now based on original code.
  try {
    const fileInput = document.getElementById(
      "image-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  } catch (e) {
    // Handle cases where document/getElementById might not be available (e.g., SSR)
    console.warn("Could not reset file input element:", e);
  }
};

/**
 * Zustand store for managing the state of image processing jobs.
 * Uses Supabase Realtime for updates and persists essential state to localStorage.
 */
const useJobStore = create<JobState>()(
  persist(
    (set, get) => ({
      // ========================================================================
      // Initial State
      // ========================================================================
      jobId: null,
      jobStatus: null,
      resultImage: null,
      error: null,
      isUploading: false,
      realtimeChannel: null,
      lastUpdated: null,

      // ========================================================================
      // Simple Setters
      // ========================================================================
      setIsUploading: (uploading) => set({ isUploading: uploading }),
      setError: (error) => set({ error: error }),
      _setJobId: (id) => set({ jobId: id, lastUpdated: Date.now() }),
      _setJobStatus: (status) =>
        set({ jobStatus: status, lastUpdated: Date.now() }),
      _setResultImage: (image) =>
        set({ resultImage: image, lastUpdated: Date.now() }),

      // ========================================================================
      // Complex Actions
      // ========================================================================

      /**
       * Handles the image upload process:
       * 1. Resets state for a new job.
       * 2. Unsubscribes from any previous job's Realtime channel.
       * 3. Sends the image file to the backend '/process' endpoint.
       * 4. On success, stores the returned job ID and initial status.
       * 5. Initiates a Realtime subscription for the new job ID via `_subscribeToJob`.
       * 6. Handles potential upload errors.
       */
      startUpload: async (file, style) => {
        if (!file) {
          set({ error: "job.errors.noFileSelected" });
          return;
        }
        // Reset relevant state before starting upload
        set({
          isUploading: true,
          error: null,
          jobId: null,
          jobStatus: null,
          resultImage: null,
        });
        get()._unsubscribeFromJob(); // Ensure any previous subscription is closed

        try {
          const formData = new FormData();
          formData.append("style", style);
          formData.append("image", file);

          // POST to the backend API to start the processing job
          const response = await apiClient.post<{ job_id: string }>(
            "/process",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          const newJobId = response.data.job_id;
          set({
            jobId: newJobId,
            jobStatus: { status: "queued", progress: 0 }, // Set initial status locally
            lastUpdated: Date.now(), // Set timestamp
          });
          // Start listening for updates for this specific job
          get()._subscribeToJob(newJobId);
        } catch (err: unknown) {
          console.error("Upload error:", err);

          let errorKey = "job.errors.uploadFailed"; // Default error key

          // Check if it's an Axios error to access response details
          if (err instanceof AxiosError && err.response) {
            const status = err.response.status;
            const backendMessage = err.response.data?.message;

            if (status === 402) {
              // Specific key for insufficient credits
              errorKey = "insufficient_credits";
            } else if (status === 429) {
              // Specific key for rate limit exceeded
              errorKey = "job.errors.rateLimitExceeded";
            } else if (backendMessage) {
              // Log the specific backend message for debugging, but use a generic key for UI
              console.warn(
                "Using generic backend error key for message:",
                backendMessage
              );
              errorKey = "job.errors.backendError";
            } else {
              // Fallback for other HTTP errors
              errorKey = `job.errors.httpError${status}`; // e.g., job.errors.httpError500
            }
          } else if (err instanceof Error) {
            // Handle non-Axios errors (network issues, etc.)
            errorKey = err.message;
          }

          set({
            error: errorKey,
            jobId: null,
            jobStatus: null,
            isUploading: false, // Stop loading on any upload API error
            lastUpdated: null, // Clear timestamp on upload failure
          });
        } finally {
          // Let the Realtime subscription handle setting isUploading to false
          // once the job starts processing or fails definitively.
          const finalState = get();
          // This finally block might run even if the try block succeeded but before
          // the async operations within it finish setting the state.
          // The error handling in the catch block now reliably sets isUploading to false.
          // We only need to ensure it's false if the API call itself failed immediately.
          if (finalState.error && !finalState.jobId) {
            // If the upload API call failed immediately (error set, no jobId),
            // ensure loading stops.
            set({ isUploading: false });
          }
        }
      },

      /**
       * Subscribes to Realtime updates for a specific job ID using Supabase channels.
       * Handles incoming messages to update job status, progress, result image, and errors.
       * Manages channel lifecycle events (subscribed, error, timeout).
       */
      _subscribeToJob: (jobId) => {
        // console.log(`Subscribing to Realtime updates for job: ${jobId}`); // Reduce noise
        get()._unsubscribeFromJob(); // Ensure only one subscription is active at a time

        // Create a Supabase channel specific to this job's updates
        const channel = supabase
          .channel(`public:jobs:job_id=eq.${jobId}`)
          .on(
            "postgres_changes", // Listen to database changes
            {
              event: "*", // Listen for INSERT and UPDATE events
              schema: "public",
              table: "jobs",
              filter: `job_id=eq.${jobId}`, // Only listen for changes to this job's row
            },
            // Callback function when a change is received
            (payload) => {
              console.log("Realtime Job Update Received:", payload);
              // Cast the payload's new data to our defined table row interface
              const updatedJob = payload.new as JobTableRow;

              if (!updatedJob) {
                console.warn("Realtime update received without new data.");
                return;
              }

              // Map the database row to our JobStatusResponse interface
              const statusData: JobStatusResponse = {
                status: updatedJob.status,
                progress: updatedJob.progress ?? 0, // Use nullish coalescing for safety
                error: updatedJob.error,
                user_id: updatedJob.user_id,
                image_id: updatedJob.image_id,
              };

              // Update the store state with the received status
              // Set isUploading to false on the first status update received.
              // Update timestamp on status update
              set({
                jobStatus: statusData,
                isUploading: false,
                error: null,
                lastUpdated: Date.now(),
              });

              // Handle terminal states: completed or failed
              if (statusData.status === "completed") {
                // Construct the result image URL if data is available
                if (statusData.user_id && statusData.image_id) {
                  // TODO: Make base image URL configurable
                  const imageUrl = `https://images.genflow.run/${statusData.user_id}/${statusData.image_id}.jpg`;
                  console.log("Job completed, setting result image:", imageUrl);
                  // Update timestamp when result is set
                  set({ resultImage: imageUrl, lastUpdated: Date.now() });
                } else {
                  // Handle missing data needed for the URL
                  console.error(
                    "Job completed but missing user_id or image_id in payload:",
                    payload
                  );
                  set({
                    error: "job.errors.resultMissingData",
                    resultImage: null,
                    lastUpdated: Date.now(), // Update timestamp even if result missing
                  });
                }
                get()._unsubscribeFromJob(); // Clean up the channel on completion
              } else if (statusData.status === "failed") {
                // Handle failed state
                console.error("Job failed:", statusData.error);

                // Determine the error key for translation
                let errorKey = "job.errors.processingFailed"; // Default key
                if (statusData.error === "Content policy violation") {
                  errorKey = "job.errors.contentPolicyViolation"; // Specific key for policy violation
                } else if (statusData.error) {
                  // Optionally, map other known backend error strings to keys here
                  // If the backend error is already a key, use it directly (be careful with this approach)
                  // For now, we use the specific policy key or a generic fallback.
                  errorKey = statusData.error; // Or keep the generic fallback: "job.errors.processingFailed"
                }

                set({
                  error: errorKey,
                  resultImage: null, // Ensure no result image on failure
                  lastUpdated: Date.now(), // Update timestamp on failure
                });
                get()._unsubscribeFromJob(); // Clean up the channel on failure
              }
            }
          )
          // Subscribe to the channel and handle connection events
          .subscribe((status, err) => {
            if (status === "SUBSCRIBED") {
              // console.log(`Realtime channel subscribed for job: ${jobId}`); // Reduce noise
            } else if (status === "CHANNEL_ERROR" || err) {
              // Handle connection errors
              console.error(
                `Realtime subscription error for job ${jobId}:`,
                err || "Channel Error"
              );
              set({
                error: "job.errors.realtimeConnectionFailed",
                isUploading: false, // Stop loading on connection error
              });
              get()._unsubscribeFromJob(); // Attempt cleanup
            } else if (status === "TIMED_OUT") {
              // Handle connection timeouts
              console.warn(`Realtime subscription timed out for job ${jobId}`);
              set({
                error: "job.errors.realtimeTimeout",
                isUploading: false,
              });
              get()._unsubscribeFromJob(); // Attempt cleanup
            } else {
              // Log other status changes
              console.log(
                `Realtime channel status for job ${jobId}: ${status}`
              );
            }
          });

        // Store the active channel object in the state
        set({ realtimeChannel: channel });
      },

      /**
       * Unsubscribes from the currently active Supabase Realtime channel, if any.
       * Uses `supabase.removeChannel` for cleanup.
       */
      _unsubscribeFromJob: () => {
        const channel = get().realtimeChannel;
        if (channel) {
          // console.log(`Unsubscribing from Realtime channel: ${channel.topic}`); // Reduce noise
          // Supabase handles the actual unsubscription internally
          supabase.removeChannel(channel).catch((error) => {
            console.error("Error removing Supabase channel:", error);
          });
          // Clear the channel from the state
          set({ realtimeChannel: null });
        }
      },

      /**
       * Fallback mechanism to fetch job results directly from the database.
       * Primarily used during hydration if the store reloads with a completed job
       * status but without the resultImage URL (e.g., page closed too quickly).
       */
      fetchJobResult: async (jobId) => {
        console.log(`Attempting to fetch result for job ${jobId} (fallback)`);
        // Avoid fetching if we already have the result
        if (get().resultImage) {
          console.log("Result image already exists, skipping fetch.");
          return;
        }
        try {
          // Query the 'jobs' table for the necessary fields
          const { data, error } = await supabase
            .from("jobs")
            .select("status, user_id, image_id")
            .eq("job_id", jobId)
            .single(); // Expect only one row

          if (error) {
            console.error("Error fetching job result (fallback):", error);
            // Avoid setting a global error that might overwrite another important one
            return;
          }

          // If the job is completed and we have the IDs, construct and set the URL
          if (
            data &&
            data.status === "completed" &&
            data.user_id &&
            data.image_id
          ) {
            // TODO: Make base image URL configurable
            const imageUrl = `https://images.genflow.run/${data.user_id}/${data.image_id}.jpg`;
            console.log("Fetched result image URL (fallback):", imageUrl);
            set({ resultImage: imageUrl, lastUpdated: Date.now() }); // Update timestamp
          } else if (data) {
            // Log if fetch was attempted for a non-completed job
            console.warn(
              `fetchJobResult called for job ${jobId}, status is ${data.status} (fallback)`
            );
          } else {
            // Log if no data was found for the job ID
            console.warn(
              `fetchJobResult found no data for job ${jobId} (fallback)`
            );
          }
        } catch (err) {
          console.error("Error in fetchJobResult (fallback):", err);
        }
      },

      /**
       * Resets the store to its initial empty state.
       * Ensures any active Realtime subscription is terminated.
       * Clears the associated file input element.
       */
      resetJobState: () => {
        console.log("Resetting job state and unsubscribing...");
        get()._unsubscribeFromJob(); // Important: Unsubscribe from any active channel
        // Use the helper function to reset state
        resetState(set);
      },
    }),
    // ========================================================================
    // Persistence Configuration (zustand/middleware)
    // ========================================================================
    {
      // Key used in localStorage
      name: "job-storage",
      // Use localStorage for persistence
      storage: createJSONStorage(() => localStorage),
      /**
       * Specifies which parts of the state should be persisted.
       * We only persist essential job identifiers and results, excluding
       * transient UI state or objects like the Realtime channel.
       */
      partialize: (state) => ({
        jobId: state.jobId,
        jobStatus: state.jobStatus, // Persist last known status
        resultImage: state.resultImage, // Persist the final image URL
        lastUpdated: state.lastUpdated, // Persist the timestamp
      }),
      /**
       * Logic executed after the store has been rehydrated from localStorage.
       * Used to automatically resume Realtime subscriptions for ongoing jobs
       * or fetch missing results for completed jobs, ONLY IF the state is not expired.
       */
      onRehydrateStorage: () => {
        // console.log("Store rehydrating..."); // Reduce noise
        return (hydratedState, error) => {
          if (error || !hydratedState) {
            console.error("Error during zustand rehydration:", error);
            resetState(useJobStore.setState); // Reset state on rehydration error
            return;
          }

          // If the job was still active (queued or processing) when the page was closed...
          if (
            hydratedState.jobStatus?.status === "queued" ||
            hydratedState.jobStatus?.status === "processing"
          ) {
            console.log(
              `Attempting to resume Realtime subscription for job ${hydratedState.jobId}`
            );
            // Use setTimeout to ensure this runs after Zustand's hydration process is fully complete
            setTimeout(() => {
              useJobStore.getState()._subscribeToJob(hydratedState.jobId!);
            }, 0);
          }
          // If the job was completed but the result image URL wasn't persisted...
          else if (
            hydratedState.jobStatus?.status === "completed" &&
            !hydratedState.resultImage
          ) {
            console.log(
              `Attempting to fetch result for completed job ${hydratedState.jobId} after hydration.`
            );
            // Use the fallback fetch mechanism
            setTimeout(() => {
              useJobStore.getState().fetchJobResult(hydratedState.jobId!);
            }, 0);
          }
        };
      },
    }
  )
);

export default useJobStore;
